import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { useAuth } from "@/hooks/auth/auth";
import { getFilenameFromContentDisposition } from "@/lib/download-file";
import { numericIdParamSchema, organizationIdParamsSchema } from "@/lib/route-params";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DownloadIcon, FileQuestionIcon, ShieldXIcon } from "lucide-react";
import { useEffect, useState } from "react";

const paramsSchema = organizationIdParamsSchema.extend({
  classId: numericIdParamSchema,
  updateId: numericIdParamSchema,
  attachmentId: numericIdParamSchema,
});

type DownloadState = "downloading" | "missing" | "denied" | "error" | "complete";

const downloadFile = (blob: Blob, contentDisposition: string | null) => {
  const filename = getFilenameFromContentDisposition(contentDisposition) ?? "attachment";
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const Route = createFileRoute(
  "/_app/organizations/$organizationId/parent/attachment-download/$classId/$updateId/$attachmentId",
)({
  params: {
    parse: (params) => paramsSchema.parse(params),
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, classId, updateId, attachmentId } = Route.useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<DownloadState>("downloading");

  useEffect(() => {
    const controller = new AbortController();

    const download = async () => {
      const response = await fetch(
        `/api/organizations/${organizationId}/parent/ParentClassUpdates/classes/${classId}/updates/${updateId}/attachments/${attachmentId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        },
      );

      if (response.status === 404) {
        setState("missing");
        return;
      }

      if (response.status === 403) {
        setState("denied");
        return;
      }

      if (!response.ok) {
        setState("error");
        return;
      }

      downloadFile(await response.blob(), response.headers.get("Content-Disposition"));
      setState("complete");
    };

    void download().catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setState("error");
      }
    });

    return () => controller.abort();
  }, [attachmentId, classId, organizationId, token, updateId]);

  const returnToUpdate = () =>
    navigate({
      to: "/organizations/$organizationId/parent/classes/$classId/updates/$updateId",
      params: { organizationId, classId, updateId },
    });
  const returnToClasses = () =>
    navigate({
      to: "/organizations/$organizationId/parent/classes",
      params: { organizationId },
    });

  const content = {
    downloading: {
      title: "Downloading attachment",
      description: "Your download will begin shortly.",
      icon: DownloadIcon,
    },
    complete: {
      title: "Download started",
      description: "If the download did not begin, please try the link in the email again.",
      icon: DownloadIcon,
    },
    missing: {
      title: "Attachment not found",
      description: "This attachment may have been removed from the update.",
      icon: FileQuestionIcon,
    },
    denied: {
      title: "Access denied",
      description: "You no longer have access to this class update.",
      icon: ShieldXIcon,
    },
    error: {
      title: "Unable to download attachment",
      description: "Please try again later or return to the update.",
      icon: FileQuestionIcon,
    },
  }[state];
  const Icon = content.icon;

  return (
    <main className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon className="size-6" />
          </div>
          <CardTitle>{content.title}</CardTitle>
          <CardDescription>{content.description}</CardDescription>
        </CardHeader>
        {state === "missing" || state === "error" ? (
          <CardFooter>
            <IconButton className="w-full" icon={<DownloadIcon />} onClick={() => void returnToUpdate()}>
              Back to update
            </IconButton>
          </CardFooter>
        ) : null}
        {state === "denied" ? (
          <CardFooter>
            <IconButton className="w-full" icon={<ShieldXIcon />} onClick={() => void returnToClasses()}>
              Back to classes
            </IconButton>
          </CardFooter>
        ) : null}
      </Card>
    </main>
  );
}
